import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  // Remove Menu and MenuItem imports since we're using custom dropdowns  
} from '@mui/material';
import {
  Add,
  ArrowUpward,
  UploadFile,
  Screenshot,
  Stop,
  CloudUpload,
  ContentCopy,
  Refresh,
  ExpandMore,
  Star,
  StarBorder,
  Edit,
  Delete,
  Download,
  Inventory
} from '@mui/icons-material';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useChatContext } from '../context/ChatContext';
import { API_BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Message, GeneratedImage, UiStreamEvent } from '../types';
import ExpertsPopup from './ExpertsPopup';
import FilePreviewModal from './FilePreviewModal';
import FileProgressOverlay from './FileProgressOverlay';
import ImageLightbox from './ImageLightbox';
import StreamingImagePreview from './StreamingImagePreview';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { usePasteHandler } from '../hooks/usePasteHandler';
import { createAuthenticatedRequestOptions } from '../utils/apiUtils';
import { validateFileType } from '../utils/fileUtils';
import ValidationMessage from './ValidationMessage';
import { SavePromptDialog } from './dialogs';
import PromptVaultDropdown from './PromptVaultDropdown';

// Configure marked to preserve multiple line breaks
marked.use({
  gfm: true,
  breaks: true
});

// Configure marked to preserve multiple line breaks
marked.use({
  gfm: true,
  breaks: true
});

// ImagePreview component to handle server-fetched images with alternating behavior
const PreloadedImageDisplay: React.FC<{
  messageId: string;
  images: GeneratedImage[];
  isStreaming: boolean;
  onImageClick: (image: GeneratedImage, index: number) => void;
  onImageLoad: () => void;
}> = ({ messageId, images, isStreaming, onImageClick, onImageLoad }) => {
  const [displayedImages, setDisplayedImages] = React.useState<GeneratedImage[]>([]);
  const [processedCount, setProcessedCount] = React.useState<number>(0);
  const [isPreloading, setIsPreloading] = React.useState<boolean>(false);
  const preloadRef = React.useRef<HTMLImageElement | null>(null);
  
  // Process images based on should_replace behavior
  React.useEffect(() => {
    if (!images || images.length === 0) {
      setDisplayedImages([]);
      setProcessedCount(0);
      return;
    }

          // For saved chats (not streaming), we want to show only the final images
      // For streaming chats, we want to process images incrementally with replacement logic
      if (!isStreaming) {
        // SAVED CHAT MODE: Calculate final images by simulating the replacement logic
        // console.log(`[PreloadedImageDisplay] Processing saved chat with ${images.length} total images`);
      
      let finalImages: GeneratedImage[] = [];
      
              // Simulate the streaming replacement logic to get the final state
        images.forEach((image, idx) => {
          // console.log(`[PreloadedImageDisplay] Processing saved image ${idx + 1}/${images.length}:`, {
          //   should_replace: image.should_replace,
          //   is_partial: image.is_partial,
          //   partial_index: image.partial_index,
          //   format: image.format
          // });
          
          if (image.should_replace) {
            // Replace the last image
            if (finalImages.length > 0) {
              finalImages[finalImages.length - 1] = image;
              // console.log(`[PreloadedImageDisplay] Replaced final image at index ${finalImages.length - 1}`);
            } else {
              finalImages.push(image);
              // console.log(`[PreloadedImageDisplay] No images to replace, added as first final image`);
            }
          } else {
            // Add as new image
            finalImages.push(image);
            // console.log(`[PreloadedImageDisplay] Added new final image, count: ${finalImages.length}`);
          }
        });
      
              // console.log(`[PreloadedImageDisplay] Final images for saved chat: ${finalImages.length} images`);
        setDisplayedImages(finalImages);
        setProcessedCount(images.length);
        
        // Trigger onImageLoad for layout updates
        if (finalImages.length > 0) {
          onImageLoad();
        }
        
      } else {
        // STREAMING MODE: Process images sequentially by index
        if (images.length <= processedCount) {
          // console.log(`[PreloadedImageDisplay] No new images to process (${images.length} <= ${processedCount})`);
          return;
        }
        
        // console.log(`[PreloadedImageDisplay] Processing streaming images from index ${processedCount} to ${images.length - 1}`);
      
      // Process new images sequentially
      let newDisplayedImages = [...displayedImages];
      
              for (let i = processedCount; i < images.length; i++) {
          const imageToProcess = images[i];
          
          // console.log(`[PreloadedImageDisplay] Processing streaming image ${i + 1}/${images.length}:`, {
          //   should_replace: imageToProcess.should_replace,
          //   is_partial: imageToProcess.is_partial,
          //   partial_index: imageToProcess.partial_index,
          //   url: imageToProcess.url?.substring(0, 50) + '...',
          //   format: imageToProcess.format
          // });
          
          if (imageToProcess.should_replace) {
            // Replace the last image
            // console.log(`[PreloadedImageDisplay] Replacing last image (should_replace=true), current displayed count: ${newDisplayedImages.length}`);
            if (newDisplayedImages.length > 0) {
              newDisplayedImages[newDisplayedImages.length - 1] = imageToProcess;
              // console.log(`[PreloadedImageDisplay] Replaced image at index ${newDisplayedImages.length - 1}, new count: ${newDisplayedImages.length}`);
            } else {
              newDisplayedImages.push(imageToProcess);
              // console.log(`[PreloadedImageDisplay] No images to replace, added as first image, new count: ${newDisplayedImages.length}`);
            }
          } else {
            // Add as new image
            // console.log(`[PreloadedImageDisplay] Adding new image (should_replace=false), current displayed count: ${newDisplayedImages.length}`);
            newDisplayedImages.push(imageToProcess);
            // console.log(`[PreloadedImageDisplay] Added new image, new count: ${newDisplayedImages.length}`);
          }
        }
      
      // Update displayed images and processed count
      setDisplayedImages(newDisplayedImages);
      setProcessedCount(images.length);
      
      // Trigger preload for the last processed image for smooth transition
      const lastProcessedImage = images[images.length - 1];
      if (lastProcessedImage) {
        const lastImageSrc = lastProcessedImage.url || `data:image/${lastProcessedImage.format};base64,${lastProcessedImage.base64}`;
        setIsPreloading(true);
        const preloadImage = new Image();
        preloadImage.onload = () => {
          setIsPreloading(false);
          onImageLoad();
        };
        preloadImage.onerror = () => {
          setIsPreloading(false);
        };
        preloadImage.src = lastImageSrc;
        preloadRef.current = preloadImage;
      }
    }
    
    return () => {
      // Cleanup preload if component unmounts or effect re-runs
      if (preloadRef.current) {
        preloadRef.current.onload = null;
        preloadRef.current.onerror = null;
      }
    };
  }, [images, isStreaming, onImageLoad, processedCount]);
  
  if (displayedImages.length === 0) {
    return null;
  }
  
  return (
    <div className="generated-images-container" style={{ marginTop: '16px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-start' }}>
        {displayedImages.map((image, index) => {
          const imageSrc = image.url || `data:image/${image.format};base64,${image.base64}`;
          return (
            <div 
              key={`${messageId}-image-${index}`}
              className="generated-image-thumbnail"
              style={{ 
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                position: 'relative',
                width: '175px',
                height: '175px',
                flexShrink: 0
              }}
              onClick={() => onImageClick(image, index)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title="Click to view full size"
            >
              <img 
                src={imageSrc}
                alt={`Generated image ${index + 1}`}
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  // Slight dimming during preload to indicate update in progress
                  opacity: isPreloading && index === displayedImages.length - 1 ? 0.8 : 1,
                  transition: 'opacity 0.2s ease-in-out'
                }}
                onLoad={onImageLoad}
              />
              {/* Streaming indicator overlay */}
              {isStreaming && index === displayedImages.length - 1 && (
                <div style={{
                  position: 'absolute',
                  top: '4px',
                  left: '4px',
                  backgroundColor: 'rgba(0, 123, 255, 0.8)',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  animation: 'pulse 1.5s infinite'
                }}>
                  {'Generating...'}
                </div>
              )}

            </div>
          );
        })}
      </div>
    </div>
  );
};

const ImagePreview: React.FC<{ 
  attachment: import('../types').FileAttachment; 
  chatId: string; 
  className: string; 
  alt: string; 
}> = ({ attachment, chatId, className, alt }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const url = `${API_BASE_URL}/api/chat/${chatId}/files/${encodeURIComponent(attachment.name)}`;
        // console.log(`[ImagePreview] Loading image for ${attachment.name} from URL: ${url}`);
        
        const response = await fetch(
          url,
          createAuthenticatedRequestOptions({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          })
        );
        
        // console.log(`[ImagePreview] Response status for ${attachment.name}: ${response.status}`);
        
        if (response.ok) {
          const data = await response.json();
          // console.log(`[ImagePreview] Successfully loaded image data for ${attachment.name}`);
          const dataUrl = `data:${data.contentType};base64,${data.content}`;
          setImageSrc(dataUrl);
          setLoading(false);
        } else {
          console.error(`[ImagePreview] Failed to load image for ${attachment.name}: ${response.status}`);
          setError(true);
          setLoading(false);
        }
      } catch (err) {
        console.error(`[ImagePreview] Error loading image for ${attachment.name}:`, err);
        setError(true);
        setLoading(false);
      }
    };

    // console.log(`[ImagePreview] Starting to load image for ${attachment.name} in chat ${chatId}`);
    loadImage();
  }, [attachment.name, chatId]);

  if (loading) {
    return (
      <div className="file-card-img-placeholder">
        <span>📷</span>
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className="file-card-img-placeholder">
        <span>📷</span>
      </div>
    );
  }

  return (
    <img 
      className={className} 
      src={imageSrc} 
      alt={alt} 
      data-filename={attachment.name} 
    />
  );
};

const ChatView: React.FC = () => {
  const {
    uiState,
    appState,
    sendMessage,
    addAttachedFile,
    removeAttachedFile,
    stopResponsePolling,
    retryMessage,
    editAndResendMessage,
    registerSpacerUpdateCallback,
    unregisterSpacerUpdateCallback,
    renameChat,
    toggleChatStar,
    deleteChat,
    showError,
    getFileUploadProgress,
    isFileUploadComplete,
    areAllUploadsComplete,
    clearFileUploadProgress,
    hasUploadsInProgress,
    validateCurrentMessage,
    clearValidationError,
    savePromptToVault,
    deletePromptFromVault
  } = useChatContext();

  const { user } = useAuth();

  const [message, setMessage] = useState('');
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<import('../types').FileAttachment | null>(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  
  // Smart scrolling state - tracks whether user is at bottom of chat
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const scrollDetectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // User scroll lock - prevents auto-scrolling for a short period after user scrolls
  const [userScrollLock, setUserScrollLock] = useState(false);
  const scrollLockTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track previous isProcessing state to detect completion transitions
  const prevIsProcessingRef = useRef<boolean>(false);
  // Track if user manually scrolled during this response (for completion scroll behavior)
  const userScrolledDuringResponseRef = useRef<boolean>(false);
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<GeneratedImage | null>(null);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  
  // Chat title dropdown and dialog states
  const [titleDropdownOpen, setTitleDropdownOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  
  // Edit message state
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
  
  // PromptVault states
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedPromptContent, setSelectedPromptContent] = useState('');
  const [savePromptDialogOpen, setSavePromptDialogOpen] = useState(false);
  const [promptVaultDropdownOpen, setPromptVaultDropdownOpen] = useState(false);
  const [promptVaultButtonRef, setPromptVaultButtonRef] = useState<HTMLButtonElement | null>(null);
  const [promptVaultPosition, setPromptVaultPosition] = useState<{ top?: number; bottom?: number; left: number }>({ left: 0 });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const attachedFilesAreaRef = useRef<HTMLDivElement>(null);
  const expertContentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const validationMessageRef = useRef<HTMLDivElement>(null);
  
  // Refs for spacer functionality - mirroring the original frontend
  const chatViewRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const chatInputContainerRef = useRef<HTMLDivElement>(null);
  const spacerUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Smart scrolling utility functions
  const checkIfUserIsAtBottom = useCallback(() => {
    const chatMessages = chatMessagesRef.current;
    if (!chatMessages) return true;
    
    const threshold = 50; // Consider user "at bottom" if within 50px of bottom
    const scrollTop = chatMessages.scrollTop;
    const scrollHeight = chatMessages.scrollHeight;
    const clientHeight = chatMessages.clientHeight;
    
    // Handle edge case where content doesn't overflow (always consider "at bottom")
    if (scrollHeight <= clientHeight) return true;
    
    const isAtBottom = scrollHeight - scrollTop - clientHeight <= threshold;
    return isAtBottom;
  }, []);

  const handleScroll = useCallback(() => {
    const isAtBottom = checkIfUserIsAtBottom();
    
    // Activate scroll lock to prevent auto-scrolling for a period after user scroll
    setUserScrollLock(true);
    if (scrollLockTimeoutRef.current) {
      clearTimeout(scrollLockTimeoutRef.current);
    }
    scrollLockTimeoutRef.current = setTimeout(() => {
      setUserScrollLock(false);
    }, 300); // 300ms lock prevents auto-scroll after user interaction
    
    // Update state immediately for scroll-up detection (no debounce when scrolling away from bottom)
    if (!isAtBottom) {
      setIsUserAtBottom(false);
      // Clear any pending timeout since we're updating immediately
      if (scrollDetectionTimeoutRef.current) {
        clearTimeout(scrollDetectionTimeoutRef.current);
        scrollDetectionTimeoutRef.current = null;
      }
    } else {
      // Only debounce when scrolling back to bottom to avoid flicker
      if (scrollDetectionTimeoutRef.current) {
        clearTimeout(scrollDetectionTimeoutRef.current);
      }
      
      scrollDetectionTimeoutRef.current = setTimeout(() => {
        setIsUserAtBottom(true);
      }, 100); // Slightly longer delay when returning to bottom
    }
  }, [checkIfUserIsAtBottom]);

  // Drag and drop functionality
  const handleFileDrop = async (files: File[]) => {
    const validFiles: File[] = [];
    
    // First, validate all file types
    for (const file of files) {
      const result = await validateFileType(file);
      if (result.valid) {
        validFiles.push(file);
      } else {
        showError(result.reason || 'Invalid file type.');
        return; // Stop processing if any file is invalid
      }
    }
    
    // Add all valid files
    for (const file of validFiles) {
      addAttachedFile(file);
    }
    
    // If files were added, update spacer and validate
    if (validFiles.length > 0) {
      // Use requestAnimationFrame to ensure all files are added to state before updates
      requestAnimationFrame(async () => {
        // Update spacer to account for new file attachments
        debouncedUpdateChatSpacer();
        
        // Validate if we have an expert selected
        if (uiState.selectedExpert) {
          await validateCurrentMessage(message);
        }
      });
    }
  };

  const { isDragOver } = useDragAndDrop({
    onDrop: handleFileDrop,
    isVisible: true
  });

  // Paste handler functionality
  const { handlePaste, resetCounters } = usePasteHandler({
    onFileAttach: addAttachedFile
  });

  // Clear upload progress when all uploads are complete
  useEffect(() => {
    const allUploadsComplete = areAllUploadsComplete();
    if (allUploadsComplete && uiState.fileUploadProgress.size > 0) {
      const timeoutId = setTimeout(() => {
        // Clear progress after a short delay to show completion state
        clearFileUploadProgress();
      }, 1500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [areAllUploadsComplete(), uiState.fileUploadProgress.size, clearFileUploadProgress]);

  // Sophisticated spacer logic mirrored from the original frontend
  const updateChatSpacer = useCallback(() => {
    const chatView = chatViewRef.current;
    const chatMessages = chatMessagesRef.current;
    const spacer = spacerRef.current;
    const chatInputDiv = chatInputContainerRef.current;
    const validationMessage = validationMessageRef.current;
    
    if (!chatView || !chatMessages || !spacer || !chatInputDiv) return;
    
    const conversationPairs = chatMessages.getElementsByClassName('conversation-pair');
    
    if (conversationPairs.length === 0) {
      spacer.style.height = '0px';
      return;
    }
    
    // For sliding window effect: size spacer so last conversation appears correctly when scrolled to bottom
    if (conversationPairs.length >= 1) {
      // Get the chat view height (viewport)
      const chatViewHeight = chatView.getBoundingClientRect().height;
      const chatInputHeight = chatInputDiv.getBoundingClientRect().height;
      
      // Account for ValidationMessage height when it's visible
      let validationMessageHeight = 0;
      if (validationMessage && uiState.showValidationError) {
        validationMessageHeight = validationMessage.getBoundingClientRect().height;
      }
      
      let availableHeight = chatViewHeight - chatInputHeight - validationMessageHeight;
      const lastConversationPairHeight = conversationPairs[conversationPairs.length - 1].getBoundingClientRect().height;
      
      // Magic number adjustment to fix spacer height issues
      const magicNumberAdjustment = 54;
      let spacerHeight = availableHeight - lastConversationPairHeight - 16 - magicNumberAdjustment;
      
      if (spacerHeight < 0) {
        spacerHeight = 0;
      }

      // During streaming, don't update spacer if content doesn't overflow yet
      // This prevents the jumping/flashing when typewriter effect completes blocks
      if (appState.isProcessing) {
        const contentHeight = chatMessages.scrollHeight;
        const viewportHeight = chatMessages.clientHeight;
        const hasOverflow = contentHeight > viewportHeight;
        
        if (!hasOverflow) {
          // No overflow yet - don't touch spacer during streaming to prevent jumping
          // console.log(`Skipping spacer update during streaming (no overflow): content=${contentHeight}px, viewport=${viewportHeight}px`);
          return;
        }
      }
      
      spacer.style.height = `${spacerHeight}px`;
      spacer.style.minHeight = `${spacerHeight}px`;
      
      // console.log(`Consistent spacer: spacer=${spacerHeight}px, lastConversationPairHeight=${lastConversationPairHeight}px, available=${availableHeight}px`);
      
      // Consistent scrolling behavior for all conversation pairs
      if (appState.isProcessing) {
        // During streaming, let the dedicated scroll interval handle scrolling to prevent conflicts
        // Don't scroll here to avoid competing with the MutationObserver scroll interval
        // console.log(`Sliding window: skipping scroll during streaming to prevent jumping`);
      } else {
        // When not streaming, only scroll if user is at bottom and no scroll lock
        if (isUserAtBottom && !userScrollLock) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
          // console.log(`Sliding window: scrolled to bottom (${chatMessages.scrollHeight})`);
        }
      }
    }

    // Debug: log auto-resize/spacer update
    // console.debug('[auto-resize] updateChatSpacer called. Total conversation pairs:', conversationPairs.length);
  }, [appState.isProcessing, uiState.showValidationError, isUserAtBottom, userScrollLock]);

  // Debounced version for streaming updates to reduce bouncing
  const debouncedUpdateChatSpacer = useCallback(() => {
    // During streaming, update immediately for real-time sliding window effect
    if (appState.isProcessing) {
      updateChatSpacer();
    } else {
      // When not streaming, only update for critical events (resize, mount) to prevent text selection interference
      // Clear any existing timeout to prevent unnecessary updates
      if (spacerUpdateTimeoutRef.current) {
        clearTimeout(spacerUpdateTimeoutRef.current);
        spacerUpdateTimeoutRef.current = null;
      }
      // Only update immediately for non-streaming cases to avoid creating interference timeouts
      updateChatSpacer();
    }
  }, [appState.isProcessing, updateChatSpacer]);

  // Set up paste event listener
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('paste', handlePaste);
      return () => {
        textarea.removeEventListener('paste', handlePaste);
      };
    }
  }, [handlePaste]);

  // Set up scroll event listener for smart scrolling
  useEffect(() => {
    const chatMessages = chatMessagesRef.current;
    if (chatMessages) {
      // Use both scroll and wheel events for more responsive detection
      const scrollHandler = handleScroll;
      const wheelHandler = () => {
        // Activate scroll lock immediately on wheel events
        setUserScrollLock(true);
        if (scrollLockTimeoutRef.current) {
          clearTimeout(scrollLockTimeoutRef.current);
        }
        scrollLockTimeoutRef.current = setTimeout(() => {
          setUserScrollLock(false);
        }, 500); // Longer lock for wheel events since they often indicate intentional scrolling
        
        // Immediately check if user is scrolling away from bottom on wheel events
        const isAtBottom = checkIfUserIsAtBottom();
        if (!isAtBottom) {
          setIsUserAtBottom(false);
          // Track that user manually scrolled during this response (for completion scroll behavior)
          // Using wheel events ensures we only track user-initiated scrolls, not programmatic scrolls
          userScrolledDuringResponseRef.current = true;
        } else {
          // User scrolled back to bottom - clear the flag so completion scroll will fire
          userScrolledDuringResponseRef.current = false;
        }
      };
      
      chatMessages.addEventListener('scroll', scrollHandler, { passive: true });
      chatMessages.addEventListener('wheel', wheelHandler, { passive: true });
      
      // Initialize user position state
      setIsUserAtBottom(checkIfUserIsAtBottom());
      
              return () => {
          chatMessages.removeEventListener('scroll', scrollHandler);
          chatMessages.removeEventListener('wheel', wheelHandler);
          if (scrollDetectionTimeoutRef.current) {
            clearTimeout(scrollDetectionTimeoutRef.current);
          }
          if (scrollLockTimeoutRef.current) {
            clearTimeout(scrollLockTimeoutRef.current);
          }
        };
    }
  }, [handleScroll, checkIfUserIsAtBottom]);

  // Auto-resize textarea functionality with dynamic height sharing
  useEffect(() => {
    const textarea = textareaRef.current;
    const attachedFilesArea = attachedFilesAreaRef.current;
    
    if (textarea) {
      const handleInput = () => {
        // Use requestAnimationFrame to batch DOM updates
        requestAnimationFrame(() => {
          // Reset height to calculate proper scrollHeight
          textarea.style.height = 'auto';
          const desiredHeight = textarea.scrollHeight;
          
          // Constants for dynamic height behavior
          const ATTACHED_FILES_MAX_HEIGHT = 220; // Default max height from CSS
          const ATTACHED_FILES_MIN_HEIGHT = 110; // Minimum height per user requirement
          const AVAILABLE_SPACE_TO_TRADE = ATTACHED_FILES_MAX_HEIGHT - ATTACHED_FILES_MIN_HEIGHT; // 110px
          const TEXTAREA_BASE_HEIGHT = 56; // Base height when no expansion needed
          
          // Calculate how much extra space the textarea wants
          const extraHeightNeeded = Math.max(0, desiredHeight - TEXTAREA_BASE_HEIGHT);
          
          if (attachedFilesArea && uiState.attachedFiles.length > 0) {
            // We have attached files, so we can trade space
            const spaceToTakeFromFiles = Math.min(extraHeightNeeded, AVAILABLE_SPACE_TO_TRADE);
            const newAttachedFilesHeight = ATTACHED_FILES_MAX_HEIGHT - spaceToTakeFromFiles;
            
            // Update attached files area height
            attachedFilesArea.style.maxHeight = newAttachedFilesHeight + 'px';
            
            if (extraHeightNeeded <= AVAILABLE_SPACE_TO_TRADE) {
              // We can accommodate the expansion by reducing attached files area
              textarea.style.height = desiredHeight + 'px';
              textarea.style.overflowY = 'hidden';
            } else {
              // We've used all available space from attached files, now textarea must scroll
              const maxTextareaHeight = TEXTAREA_BASE_HEIGHT + AVAILABLE_SPACE_TO_TRADE;
              textarea.style.height = maxTextareaHeight + 'px';
              textarea.style.overflowY = 'auto';
            }
          } else {
            // No attached files, use standard behavior with a reasonable max height
            const maxHeight = 300; // Same as original frontend CSS
            
            if (desiredHeight >= maxHeight) {
              textarea.style.height = maxHeight + 'px';
              textarea.style.overflowY = 'auto';
            } else {
              textarea.style.height = desiredHeight + 'px';
              textarea.style.overflowY = 'hidden';
            }
          }
          
          // Update spacer in the same frame to prevent layout shifts
          updateChatSpacer();
        });
      };

      // Set up the input event listener
      textarea.addEventListener('input', handleInput);
      
      // Initial resize
      handleInput();
      
      return () => {
        textarea.removeEventListener('input', handleInput);
      };
    }
  }, [updateChatSpacer, uiState.attachedFiles.length]);

  // Also trigger resize when message content changes
  useEffect(() => {
    const textarea = textareaRef.current;
    const attachedFilesArea = attachedFilesAreaRef.current;
    
    if (textarea) {
      // Use requestAnimationFrame to batch DOM updates
      requestAnimationFrame(() => {
        // Reset height to auto first
        textarea.style.height = 'auto';
        const desiredHeight = textarea.scrollHeight;
        
        // Constants for dynamic height behavior
        const ATTACHED_FILES_MAX_HEIGHT = 220; // Default max height from CSS
        const ATTACHED_FILES_MIN_HEIGHT = 110; // Minimum height per user requirement
        const AVAILABLE_SPACE_TO_TRADE = ATTACHED_FILES_MAX_HEIGHT - ATTACHED_FILES_MIN_HEIGHT; // 110px
        const TEXTAREA_BASE_HEIGHT = 56; // Base height when no expansion needed
        
        // Calculate how much extra space the textarea wants
        const extraHeightNeeded = Math.max(0, desiredHeight - TEXTAREA_BASE_HEIGHT);
        
        if (attachedFilesArea && uiState.attachedFiles.length > 0) {
          // We have attached files, so we can trade space
          const spaceToTakeFromFiles = Math.min(extraHeightNeeded, AVAILABLE_SPACE_TO_TRADE);
          const newAttachedFilesHeight = ATTACHED_FILES_MAX_HEIGHT - spaceToTakeFromFiles;
          
          // Update attached files area height
          attachedFilesArea.style.maxHeight = newAttachedFilesHeight + 'px';
          
          if (extraHeightNeeded <= AVAILABLE_SPACE_TO_TRADE) {
            // We can accommodate the expansion by reducing attached files area
            textarea.style.height = desiredHeight + 'px';
            textarea.style.overflowY = 'hidden';
          } else {
            // We've used all available space from attached files, now textarea must scroll
            const maxTextareaHeight = TEXTAREA_BASE_HEIGHT + AVAILABLE_SPACE_TO_TRADE;
            textarea.style.height = maxTextareaHeight + 'px';
            textarea.style.overflowY = 'auto';
          }
        } else {
          // No attached files, use standard behavior with a reasonable max height
          const maxHeight = 300; // Same as original frontend CSS
          
          if (desiredHeight >= maxHeight) {
            textarea.style.height = maxHeight + 'px';
            textarea.style.overflowY = 'auto';
          } else {
            textarea.style.height = desiredHeight + 'px';
            textarea.style.overflowY = 'hidden';
          }
        }
        
        // Update spacer in the same frame to prevent layout shifts
        updateChatSpacer();
      });
    }
  }, [message, updateChatSpacer, uiState.attachedFiles.length]);

  // Reset attached files area height when no files are attached
  useEffect(() => {
    const attachedFilesArea = attachedFilesAreaRef.current;
    if (attachedFilesArea && uiState.attachedFiles.length === 0) {
      // Reset to default max height when no files are attached
      attachedFilesArea.style.maxHeight = '220px';
    }
  }, [uiState.attachedFiles.length]);

  // Update spacer whenever messages change during active processing or initial load
  useEffect(() => {
    // Only update spacer during active processing or when no chat is loaded (initial state)
    if (appState.isProcessing || !appState.currentChat) {
      debouncedUpdateChatSpacer();
    }
  }, [appState.currentChat?.messages, appState.isProcessing, debouncedUpdateChatSpacer]);

  // Scroll to bottom when a saved chat is loaded
  useEffect(() => {
    if (appState.currentChat && !appState.isProcessing) {
      // Debug: Log file attachments when chat is loaded
      // console.log(`[ChatView] Chat loaded: ${appState.currentChat.title}`);
      if (appState.currentChat.messages) {
        appState.currentChat.messages.forEach((message, index) => {
          if (message.attachments && message.attachments.length > 0) {
            // console.log(`[ChatView] Message ${index} has ${message.attachments.length} attachments:`);
            message.attachments.forEach((att, attIndex) => {
              // console.log(`  Attachment ${attIndex}:`, {
              //   name: att.name,
              //   size: att.size,
              //   type: att.type,
              //   hasFile: !!att.file,
              //   hasUrl: !!att.url,
              //   hasKey: !!att.key,
              //   url: att.url,
              //   key: att.key,
              //   fileType: att.file ? typeof att.file : 'undefined',
              //   fileSize: att.file ? att.file.size : 'N/A'
              // });
              // console.log(`  Full attachment object:`, att);
              if (att.file) {
                // console.log(`  File object details:`, {
                //   fileName: att.file.name,
                //   fileSize: att.file.size,
                //   fileType: att.file.type,
                //   isActualFile: att.file instanceof File,
                //   constructor: att.file.constructor.name
                // });
              }
            });
          }
        });
      }
      
      const chatMessages = chatMessagesRef.current;
      if (chatMessages) {
        // Reset smart scrolling state and scroll lock when loading a new chat
        setIsUserAtBottom(true);
        setUserScrollLock(false);
        if (scrollLockTimeoutRef.current) {
          clearTimeout(scrollLockTimeoutRef.current);
          scrollLockTimeoutRef.current = null;
        }
        
        // Use a small delay to ensure DOM has updated with the new messages
        setTimeout(() => {
          chatMessages.scrollTop = chatMessages.scrollHeight;
          // console.log(`Scrolled to bottom after loading chat: ${appState.currentChat?.title}`);
        }, 100);
      }
    }
  }, [appState.currentChat?.id]); // Watch for chat ID changes to detect when a new/different chat is loaded

    // Dedicated effect to ensure chat stays scrolled to bottom during streaming
  useEffect(() => {
    if (!appState.isProcessing || !chatMessagesRef.current) return;

    const chatMessages = chatMessagesRef.current;
    let spacerUpdateTimeout: NodeJS.Timeout | null = null;
    let observer: MutationObserver | null = null;
    
    // Function to intelligently scroll to bottom during streaming (smart scrolling)
    const forceScrollToBottom = () => {
      // NEVER auto-scroll if user has a scroll lock active (recent manual scroll activity)
      if (userScrollLock || !isUserAtBottom) {
        return;
      }
      
      if (appState.isProcessing) {
        // During streaming, only scroll if there's actual overflow
        const contentHeight = chatMessages.scrollHeight;
        const viewportHeight = chatMessages.clientHeight;
        const hasOverflow = contentHeight > viewportHeight;
        
        if (hasOverflow) {
          // Only scroll when there's overflow
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
        // If no overflow, don't scroll - let content naturally appear
      } else {
        // When not streaming, only scroll if user is near bottom
        const isAtBottom = chatMessages.scrollHeight - chatMessages.scrollTop - chatMessages.clientHeight < 10;
        if (isAtBottom) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      }
    };

    // Debounce spacer updates to prevent layout thrashing
    const debouncedSpacerUpdate = (delay: number = 100) => {
      if (spacerUpdateTimeout) {
        clearTimeout(spacerUpdateTimeout);
      }
      spacerUpdateTimeout = setTimeout(() => {
        updateChatSpacer();
      }, delay);
    };



    // Set up MutationObserver for content changes (always active for spacer updates)
    observer = new MutationObserver((mutations) => {
      let shouldScroll = false;
      let shouldUpdateSpacer = false;
      let hasImageChanges = false;
      let hasLayoutChanges = false;
      
      mutations.forEach((mutation) => {
        // Check for image-related mutations
        if (mutation.type === 'childList') {
          // Check if images were added or modified
          Array.from(mutation.addedNodes).forEach((node) => {
            if (node instanceof Element) {
              if (node.tagName === 'IMG' || node.querySelector('img')) {
                hasImageChanges = true;
              }
            }
          });
        }
        
        // Check if any text content changed (typewriting effect)
        if (mutation.type === 'characterData') {
          shouldScroll = true;
          shouldUpdateSpacer = true;
        }
        // Check if any child nodes were added/modified
        else if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldScroll = true;
          shouldUpdateSpacer = true;
          hasLayoutChanges = true;
        }
        // Check if any attributes changed (like style updates)
        else if (mutation.type === 'attributes') {
          shouldScroll = true;
          // Only update spacer for certain attribute changes that might affect layout
          if (mutation.attributeName === 'style' || mutation.attributeName === 'class') {
            shouldUpdateSpacer = true;
            hasLayoutChanges = true;
          }
        }
      });
      
      // Only auto-scroll if user is at bottom and no scroll lock
      if (shouldScroll && isUserAtBottom && !userScrollLock) {
        forceScrollToBottom();
      }
      
      // Only update spacer during active streaming to prevent text selection interference
      if (shouldUpdateSpacer && appState.isProcessing) {
        if (hasImageChanges) {
          // For image changes, wait longer to ensure proper loading and prevent flashing
          debouncedSpacerUpdate(150);
        } else if (hasLayoutChanges) {
          // For layout changes, be more conservative during streaming
          debouncedSpacerUpdate(75);
        } else {
          // For text changes during streaming, update spacer less frequently to prevent jumping
          debouncedSpacerUpdate(150);
        }
      }
    });

    // Observe the entire chat messages container for any changes
    observer.observe(chatMessages, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class'] // Watch for style and class changes
    });

    // Initial scroll to bottom when streaming starts (if user is at bottom)
    if (isUserAtBottom && !userScrollLock) {
      forceScrollToBottom();
    }

    // Cleanup
    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (spacerUpdateTimeout) {
        clearTimeout(spacerUpdateTimeout);
      }
    };
  }, [appState.isProcessing, updateChatSpacer]); // Removed isUserAtBottom and userScrollLock from dependencies to prevent re-creation

  // Separate effect to dynamically manage scroll interval based on user position
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (!appState.isProcessing) return;
    
    const shouldAutoScroll = isUserAtBottom && !userScrollLock;
    
    // Clear existing interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    
    // Only create new interval if we should auto-scroll
    if (shouldAutoScroll) {
      const chatMessages = chatMessagesRef.current;
      if (chatMessages) {
        scrollIntervalRef.current = setInterval(() => {
          // Double-check conditions before scrolling
          if (isUserAtBottom && !userScrollLock && appState.isProcessing) {
            const contentHeight = chatMessages.scrollHeight;
            const viewportHeight = chatMessages.clientHeight;
            const hasOverflow = contentHeight > viewportHeight;
            
            if (hasOverflow) {
              chatMessages.scrollTop = chatMessages.scrollHeight;
            }
          }
        }, 200);
      }
    }
    
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [appState.isProcessing, isUserAtBottom, userScrollLock]);

  // Effect to scroll user's message to top when AI response completes
  useEffect(() => {
    // Detect transition from processing to not processing (response complete)
    if (prevIsProcessingRef.current && !appState.isProcessing) {
      // Response just completed - check if we should scroll to show user's message at top
      if (!userScrolledDuringResponseRef.current) {
        const chatMessages = chatMessagesRef.current;
        if (chatMessages) {
          // Find the last conversation pair (which contains the user's message + AI response)
          const conversationPairs = chatMessages.getElementsByClassName('conversation-pair');
          if (conversationPairs.length > 0) {
            const lastConversationPair = conversationPairs[conversationPairs.length - 1];
            // Find the user message row within this conversation pair
            const userMessageRow = lastConversationPair.querySelector('.user-message-row');
            if (userMessageRow) {
              // Small delay to ensure DOM is fully updated with the complete response
              setTimeout(() => {
                // Scroll so the user's message is at the top of the viewport
                // Using scrollIntoView with block: 'start' positions the element at the top
                userMessageRow.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 150);
            }
          }
        }
      }
    }
    
    // When a new response starts, reset the tracking flag
    if (!prevIsProcessingRef.current && appState.isProcessing) {
      userScrolledDuringResponseRef.current = false;
    }
    
    // Update the ref with current state for next render
    prevIsProcessingRef.current = appState.isProcessing;
  }, [appState.isProcessing]);

  // Update spacer on window resize - just like the original frontend
  useEffect(() => {
    const handleResize = () => {
      debouncedUpdateChatSpacer();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [debouncedUpdateChatSpacer]);

  // Update spacer on initial mount and when component updates
  useEffect(() => {
    debouncedUpdateChatSpacer();
  }, [debouncedUpdateChatSpacer]);

  // Register spacer callback with context for streaming updates - mirroring original frontend
  useEffect(() => {
    registerSpacerUpdateCallback(updateChatSpacer);
    return () => {
      unregisterSpacerUpdateCallback();
    };
  }, [registerSpacerUpdateCallback, unregisterSpacerUpdateCallback, updateChatSpacer]);

  // Cleanup timeouts and caches on unmount
  useEffect(() => {
    return () => {
      if (spacerUpdateTimeoutRef.current) {
        clearTimeout(spacerUpdateTimeoutRef.current);
      }
      if (scrollLockTimeoutRef.current) {
        clearTimeout(scrollLockTimeoutRef.current);
      }
      if (scrollDetectionTimeoutRef.current) {
        clearTimeout(scrollDetectionTimeoutRef.current);
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
      // Clear content render cache to prevent memory leaks
      contentRenderCache.current.clear();
    };
  }, []);

  // Focus textarea on initial mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Focus textarea after AI responses complete
  useEffect(() => {
    if (!appState.isProcessing && textareaRef.current) {
      // Small delay to ensure DOM updates are complete
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
  }, [appState.isProcessing]);

  // Validate message when text or files change
  useEffect(() => {
    const validateMessage = async () => {
      if (message.trim() || uiState.attachedFiles.length > 0) {
        await validateCurrentMessage(message);
      } else {
        clearValidationError();
      }
    };

    const debounceTimeout = setTimeout(validateMessage, 150);
    return () => clearTimeout(debounceTimeout);
  }, [message, uiState.attachedFiles, validateCurrentMessage, clearValidationError]);

  // Close attach dropdown if clicking outside
  React.useEffect(() => {
    if (!attachMenuOpen) return;
    const handleDocumentClick = (e: MouseEvent) => {
      const dropdown = document.getElementById('chat-attach-dropdown');
      const attachButton = document.querySelector('.attach-button');
      
      // Don't close if clicking on the dropdown itself or the attach button
      if (dropdown && !dropdown.contains(e.target as Node) && 
          attachButton && !attachButton.contains(e.target as Node)) {
        setAttachMenuOpen(false);
      }
    };
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && attachMenuOpen) {
        setAttachMenuOpen(false);
        // Remove focus from the attach button to prevent the black border
        const attachButton = document.querySelector('.attach-button') as HTMLButtonElement;
        if (attachButton) {
          attachButton.blur();
        }
      }
    };
    
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [attachMenuOpen]);

  // FIXME: Commented out this useEffect as it was causing unwanted scrolling to top
  // after each expert response completion. This was interfering with the user experience
  // for follow-up questions where users expect to stay at the bottom of the conversation.
  // The existing sliding window logic already handles proper scrolling during streaming.
  /*
  useEffect(() => {
    // Only scroll to top when not streaming to avoid conflicts with sliding window effect
    if (!appState.isProcessing) {
      scrollToTop();
    }
  }, [appState.currentChat?.messages, appState.isProcessing]);
  */

  const handleSubmit = async () => {
    if (!message.trim() || !uiState.selectedExpert) return;
    
    // Validate message before submission
    const validationResult = await validateCurrentMessage(message);
    if (!validationResult.isValid) {
      return; // Don't submit if validation fails
    }
    
    // Reset smart scrolling state and scroll lock when user submits a message
    setIsUserAtBottom(true);
    setUserScrollLock(false);
    if (scrollLockTimeoutRef.current) {
      clearTimeout(scrollLockTimeoutRef.current);
      scrollLockTimeoutRef.current = null;
    }
    
    // If editing a message, use editAndResendMessage instead of sendMessage
    if (editingMessageIndex !== null) {
      const indexToEdit = editingMessageIndex;
      setEditingMessageIndex(null);
      setMessage('');
      resetCounters();
      await editAndResendMessage(indexToEdit, message);
      return;
    }
    
    await sendMessage(message, uiState.attachedFiles);
    setMessage('');
    // Reset paste counters after submission, similar to previous frontend
    resetCounters();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Don't submit if already processing or if submit would be disabled
      if (!appState.isProcessing && !isSubmitDisabled) {
        handleSubmit();
      }
    }
  };

  const handleAttachClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Toggle the menu: if it's open, close it; if closed, open it
    setAttachMenuOpen(!attachMenuOpen);
  };

  const handleAttachClose = () => {
    setAttachMenuOpen(false);
    // Remove focus from the attach button to prevent the black border
    const attachButton = document.querySelector('.attach-button') as HTMLButtonElement;
    if (attachButton) {
      attachButton.blur();
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
    handleAttachClose();
  };

  const handleScreenshot = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        showError('Screen capture is not supported in this browser.');
        handleAttachClose();
        return;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true
      });

      // Create a video element to capture the frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      video.addEventListener('loadedmetadata', () => {
        // Create canvas to capture the frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          
          // Convert to blob and create file
          canvas.toBlob((blob) => {
            if (blob) {
              const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
              const file = new File([blob], `screenshot-${timestamp}.png`, { type: 'image/png' });
              addAttachedFile(file);
            }
            
            // Stop the stream
            stream.getTracks().forEach(track => track.stop());
          }, 'image/png');
        } else {
          stream.getTracks().forEach(track => track.stop());
          showError('Failed to capture screenshot.');
        }
      });

      handleAttachClose();
    } catch (error) {
      console.error('Screenshot error:', error);
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          showError('Screen capture permission was denied.');
        } else if (error.name === 'NotSupportedError') {
          showError('Screen capture is not supported in this browser.');
        } else {
          showError('Failed to capture screenshot: ' + error.message);
        }
      } else {
        showError('Failed to capture screenshot.');
      }
      handleAttachClose();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    let filesAdded = false;
    
    if (files) {
      for (const file of Array.from(files)) {
        const result = await validateFileType(file);
        if (result.valid) {
          addAttachedFile(file);
          filesAdded = true;
        } else {
          showError(result.reason || 'Invalid file type.');
          break;
        }
      }
    }
    
    // Update spacer if files were added
    if (filesAdded) {
      requestAnimationFrame(() => {
        debouncedUpdateChatSpacer();
      });
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    removeAttachedFile(index);
    
    // Update spacer and re-evaluate validation after file removal
    // Use requestAnimationFrame to ensure the file is removed from state first
    requestAnimationFrame(async () => {
      // Update spacer to account for removed file attachment
      debouncedUpdateChatSpacer();
      
      // Re-evaluate validation if we have an expert selected
      // This ensures validation errors are cleared right away when files are removed to fix issues
      if (uiState.selectedExpert) {
        await validateCurrentMessage(message);
      }
    });
  };

  const handleStopResponse = () => {
    stopResponsePolling();
  };

  const handleFilePreview = (file: File) => {
    setPreviewFile(file);
    setPreviewAttachment(null);
    setPreviewModalOpen(true);
  };

  const handleAttachmentPreview = (attachment: import('../types').FileAttachment) => {
    setPreviewFile(null);
    setPreviewAttachment(attachment);
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewFile(null);
    setPreviewAttachment(null);
  };

  const handleImageLightboxOpen = (image: GeneratedImage, index: number) => {
    setLightboxImage(image);
    setLightboxImageIndex(index);
    setLightboxOpen(true);
  };

  const handleImageLightboxClose = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
    setLightboxImageIndex(0);
  };

  // Helper function to find file in attached files by filename
  const findFileByName = (filename: string): File | null => {
    return uiState.attachedFiles.find(file => file.name === filename) || null;
  };

  // Helper function to get file from attachment (either from UI state or stored file)
  const getFileFromAttachment = (attachment: import('../types').FileAttachment): File | null => {
    // First try to find in current UI attached files
    const uiFile = findFileByName(attachment.name);
    if (uiFile) return uiFile;
    
    // Then check if the attachment has the file stored directly and it's a valid File object
    if (attachment.file && attachment.file instanceof File && attachment.file.size > 0) {
      return attachment.file;
    }
    
    // console.log(`[getFileFromAttachment] No valid local file found for ${attachment.name}:`, {
    //   hasFile: !!attachment.file,
    //   isFileObject: attachment.file instanceof File,
    //   fileSize: attachment.file?.size,
    //   hasKey: !!attachment.key
    // });
    
    return null;
  };

  // Helper function to get file from attachment with server fetching capability
  const getFileFromAttachmentAsync = async (attachment: import('../types').FileAttachment): Promise<File | null> => {
    // console.log(`[getFileFromAttachmentAsync] Called for file: ${attachment.name}`);
    // console.log(`[getFileFromAttachmentAsync] Attachment details:`, {
    //   name: attachment.name,
    //   size: attachment.size,
    //   type: attachment.type,
    //   hasFile: !!attachment.file,
    //   hasUrl: !!attachment.url,
    //   hasKey: !!attachment.key
    // });
    
    // First try to get file locally
    const localFile = getFileFromAttachment(attachment);
    if (localFile) {
      // console.log(`[getFileFromAttachmentAsync] Found local file for ${attachment.name}`);
      return localFile;
    }
    
    // console.log(`[getFileFromAttachmentAsync] No local file found for ${attachment.name}, attempting server fetch`);
    
    // If no local file and we have a chat ID, try to fetch from server
    if (appState.currentChat?.id) {
      try {
        const url = `${API_BASE_URL}/api/chat/${appState.currentChat.id}/files/${encodeURIComponent(attachment.name)}`;
        // console.log(`[getFileFromAttachmentAsync] Fetching from URL: ${url}`);
        
        const response = await fetch(
          url,
          createAuthenticatedRequestOptions({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          })
        );
        
        // console.log(`[getFileFromAttachmentAsync] Server response status: ${response.status}`);
        
        if (response.ok) {
          const data = await response.json();
          // console.log(`[getFileFromAttachmentAsync] Successfully fetched file data for ${attachment.name}`, {
          //   contentType: data.contentType,
          //   contentLength: data.content?.length
          // });
          
          // Convert base64 content back to a File object
          const base64Data = data.content;
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: data.contentType });
          const file = new File([blob], attachment.name, { type: data.contentType });
          // console.log(`[getFileFromAttachmentAsync] Created File object for ${attachment.name}:`, {
          //   name: file.name,
          //   size: file.size,
          //   type: file.type
          // });
          return file;
        } else {
          const errorText = await response.text();
          console.error(`[getFileFromAttachmentAsync] Server error for ${attachment.name}: ${response.status} - ${errorText}`);
        }
      } catch (err) {
        console.error(`[getFileFromAttachmentAsync] Failed to fetch file ${attachment.name} from server:`, err);
      }
    } else {
      // console.log(`[getFileFromAttachmentAsync] No chat ID available for fetching ${attachment.name}`);
    }
    
    // console.log(`[getFileFromAttachmentAsync] Failed to get file ${attachment.name}, returning null`);
    return null;
  };

  const getExpertInitials = (archetype: string): string => {
    const words = archetype.split(' ');
    if (words.length >= 2) {
      return words[0][0] + words[1][0];
    }
    return archetype.substring(0, 2).toUpperCase();
  };

  // Function to get user initials from display name
  const getUserInitials = (): string => {
    if (!user?.userDisplayName) return 'XX';
    
    const nameParts = user.userDisplayName.trim().split(' ');
    if (nameParts.length >= 2) {
      // Get first and last name initials
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else if (nameParts.length === 1) {
      // Single name, use first two characters
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return 'XX';
  };

  const getAvatarColor = (expertId: string): string => {
    const colors = [
      'var(--avatar-color-1)', 'var(--avatar-color-2)', 'var(--avatar-color-3)', 
      'var(--avatar-color-4)', 'var(--avatar-color-5)', 'var(--avatar-color-6)',
      'var(--avatar-color-7)', 'var(--avatar-color-8)', 'var(--avatar-color-9)'
    ];
    const index = expertId?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0;
    return colors[index % colors.length];
  };

  // Content rendering cache to prevent unnecessary DOM updates
  const contentRenderCache = useRef<Map<string, React.ReactNode>>(new Map());
  
  // Stable callback for image loading to prevent unnecessary re-renders
  const handleImageLoad = useCallback(() => {
    // Only update spacer during active processing to prevent text selection interference
    if (appState.isProcessing) {
      updateChatSpacer();
    }
  }, [appState.isProcessing, updateChatSpacer]);
  
  const renderMessageContent = useCallback((content: string, isUser: boolean) => {
    // Create cache key from content and user type
    const cacheKey = `${isUser ? 'user' : 'expert'}-${content}`;
    
    // Check cache first
    if (contentRenderCache.current.has(cacheKey)) {
      return contentRenderCache.current.get(cacheKey);
    }
    
    let renderedContent: React.ReactNode;
    
    if (isUser) {
      // For user messages, just render as plain text with line breaks
      renderedContent = (
        <div style={{
          fontSize: '14px',
          lineHeight: '1.5',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {content}
        </div>
      );
    } else {
      // For AI messages, render as markdown
      try {
        const isReasoning = (() => {
          try {
            // Heuristic: when streaming and message flagged, or when content starts with [thinking]
            if (appState.currentChat && appState.currentChat.messages) {
              const msg = appState.currentChat.messages.find(m => !isUser && typeof m.content === 'string' && m.content === content);
              if (msg && (msg as any).isReasoning) return true;
            }
          } catch {}
          return false;
        })();
        // Remove sandbox: links entirely (markdown links, HTML anchors, reference definitions+uses, bare sandbox: URLs)
        // 1) Strip reference definitions while collecting their IDs
        const refDefRegex = /^\s*\[([^\]]+?)\]:\s*sandbox:[^\s]+.*$/gim;
        const collectedRefIds: string[] = [];
        let tmpContent = content.replace(refDefRegex, (_m, id) => {
          try { collectedRefIds.push(String(id).trim().toLowerCase()); } catch {}
          return '';
        });
        // 2) Remove reference uses for collected IDs: [text][id], ![alt][id], and shortcut [id]
        if (collectedRefIds.length) {
          const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const group = collectedRefIds.map(esc).join('|');
          const refUseRegex = new RegExp(`(?:!\\[[^\\]]*?\\]\\s*\\[\\s*(?:${group})\\s*\\])|(?:\\[[^\\]]*?\\]\\s*\\[\\s*(?:${group})\\s*\\])|(?:\\[\\s*(?:${group})\\s*\\])`, 'gim');
          tmpContent = tmpContent.replace(refUseRegex, '');
        }
        // 3) Remove inline sandbox links and images, HTML anchors, angle-bracketed links, and bare sandbox tokens
        const contentWithoutSandbox = tmpContent
          // Markdown links: [text](sandbox:/...)
          .replace(/\[[^\]]*?\]\s*\(\s*sandbox:[^)]+?\)/gim, '')
          // Markdown images: ![alt](sandbox:/...)
          .replace(/!\[[^\]]*?\]\s*\(\s*sandbox:[^)]+?\)/gim, '')
          // HTML anchors: <a href="sandbox:/...">text</a>
          .replace(/<a\b[^>]*\bhref\s*=\s*["']\s*sandbox:[^"']*["'][^>]*>.*?<\/a>/gim, '')
          // Angle-bracket autolink: <sandbox:/...>
          .replace(/<\s*sandbox:[^>]+>/gim, '')
          // Bare sandbox URLs: sandbox:/...
          .replace(/\bsandbox:[^\s)\]]+/gim, '');
        // Preserve multiple line breaks
        const preprocessedContent = contentWithoutSandbox;
        
        // Use the marked function directly which should return a string
        let htmlContent: string;
        const result = marked(preprocessedContent);
        if (typeof result === 'string') {
          htmlContent = result;
        } else {
          // If it's a Promise, fall back to plain text
          throw new Error('Async marked not supported');
        }
        
        const sanitizedHtml = DOMPurify.sanitize(htmlContent);
        
        renderedContent = (
          <div
            style={{
              fontSize: '14px',
              lineHeight: '1.5',
              color: isReasoning ? '#6b7280' : undefined // gray-500 for transient reasoning
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        );
      } catch (error) {
        // Fallback to plain text if markdown parsing fails
        renderedContent = (
          <div style={{
            fontSize: '14px',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {content}
          </div>
        );
      }
    }
    
    // Cache the rendered content
    contentRenderCache.current.set(cacheKey, renderedContent);
    
    // Limit cache size to prevent memory leaks
    if (contentRenderCache.current.size > 100) {
      const firstKey = contentRenderCache.current.keys().next().value;
      contentRenderCache.current.delete(firstKey);
    }
    
    return renderedContent;
  }, []);

  const handleCopyExpertMessage = async (index: number) => {
    const el = expertContentRefs.current[index];
    if (!el) return;
    // Clone the node to avoid modifying the live DOM
    const clone = el.cloneNode(true) as HTMLElement;
    // Inline important styles for MS Word
    clone.style.background = '#fff';
    clone.style.borderRadius = '12px';
    clone.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
    clone.style.padding = '12px 16px';
    clone.style.fontFamily = 'Montserrat, "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';
    clone.style.fontSize = '14px';
    clone.style.color = '#1D1E1E';
    // Inline code block styles
    clone.querySelectorAll('pre').forEach(pre => {
      (pre as HTMLElement).style.background = '#f1f1f1';
      (pre as HTMLElement).style.padding = '8px';
      (pre as HTMLElement).style.borderRadius = '4px';
      (pre as HTMLElement).style.fontFamily = "'Courier New', Courier, monospace";
      (pre as HTMLElement).style.fontSize = '14px';
    });
    // Inline code styles
    clone.querySelectorAll('code').forEach(code => {
      (code as HTMLElement).style.fontFamily = "'Courier New', Courier, monospace";
      (code as HTMLElement).style.fontSize = '14px';
      (code as HTMLElement).style.background = '#f1f1f1';
      (code as HTMLElement).style.padding = '2px 4px';
      (code as HTMLElement).style.borderRadius = '4px';
    });
    // Get HTML and plain text
    const html = clone.outerHTML;
    const text = el.innerText;
    // Write to clipboard
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        })
      ]);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    } catch (err) {
      navigator.clipboard.writeText(text);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    }
  };

  // Remove the global tracking approach and replace with per-message logic

  // Remove these:
  // const renderedAttachmentsRef = useRef<Set<string>>(new Set());
  // const getAttachmentKey = (attachment: any) => { ... };
  // const shouldRenderAttachment = (attachment: any) => { ... };
  // useEffect(() => { renderedAttachmentsRef.current.clear(); }, [appState.currentChat?.messages]);

  // Replace with a simple function to check if this is the first message with this attachment:
  const isFirstMessageWithAttachment = (attachment: any, currentIndex: number, messages: Message[]) => {
    const attachmentKey = `${attachment.name}-${attachment.size || 0}`;
    
    // Check if any previous message has this same attachment
    for (let i = 0; i < currentIndex; i++) {
      const prevMsg = messages[i];
      if (prevMsg.attachments) {
        for (const prevAttachment of prevMsg.attachments) {
          const prevKey = `${prevAttachment.name}-${prevAttachment.size || 0}`;
          if (prevKey === attachmentKey) {
            return false; // Found this attachment in a previous message
          }
        }
      }
    }
    return true; // This is the first message with this attachment
  };

  // Memoized message rendering function (optimized to prevent unnecessary re-renders)
  const renderMessage = useCallback((msg: Message, index: number, messages: Message[]): React.ReactNode => {
    const isUser = msg.role === 'user';
    const isStreaming = msg.isStreaming || false;
    const isLastExpertMessage = !isUser && index === messages.length - 1;

    if (isUser) {
      // User messages: gray bubble with green avatar inside on the left of the text
      return (
        <div
          key={`message-${msg.id || msg.timestamp || msg.role}-${index}`}
          className={`message-row user-message-row`}
        >
          {/* File Attachments - Render above user message content */}
          {msg.attachments && msg.attachments.length > 0 && (
            <div className="file-cards-container" style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
              {msg.attachments.map((attachment, attIndex) => {
                if (!isFirstMessageWithAttachment(attachment, index, messages)) {
                  return null;
                }
                // Get file extension for type label
                let ext = attachment.name.split('.').pop()?.toUpperCase() || 'FILE';
                if (ext === attachment.name) ext = 'FILE';
                
                // Check if this is an image file
                const isImageFile = attachment.type?.startsWith('image/') || 
                  ['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP', 'BMP', 'SVG'].includes(ext);
                
                if (isImageFile) {
                  const file = getFileFromAttachment(attachment);
                  const useObj = file || attachment;
                  const progress = getFileUploadProgress(attachment.name);
                  const isComplete = isFileUploadComplete(attachment.name);
                  const showOverlay = progress >= 1 && (!areAllUploadsComplete() || progress < 100);
                  
                  return (
                    <div 
                      key={attIndex} 
                      className="file-card image-card" 
                      tabIndex={0} 
                      role="button" 
                      aria-label={`Preview ${attachment.name}`} 
                      data-filename={attachment.name}
                      onClick={async () => {
                        const file = await getFileFromAttachmentAsync(attachment);
                        if (file) {
                          handleFilePreview(file);
                        } else {
                          handleAttachmentPreview(attachment);
                        }
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          const file = await getFileFromAttachmentAsync(attachment);
                          if (file) {
                            handleFilePreview(file);
                          } else {
                            handleAttachmentPreview(attachment);
                          }
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {file ? (
                        <img 
                          className="file-card-img" 
                          src={allFileBlobUrls[`message-${index}-${attIndex}-${file.name}-${file.size}`] || URL.createObjectURL(file)} 
                          alt={file.name} 
                          data-filename={file.name} 
                        />
                      ) : (
                        <ImagePreview 
                          attachment={attachment} 
                          chatId={appState.currentChat?.id || ''} 
                          className="file-card-img" 
                          alt={attachment.name} 
                        />
                      )}
                      {showOverlay && (
                        <FileProgressOverlay 
                          progress={progress} 
                          isComplete={isComplete} 
                          fileName={attachment.name} 
                        />
                      )}
                    </div>
                  );
                } else {
                  const progress = getFileUploadProgress(attachment.name);
                  const isComplete = isFileUploadComplete(attachment.name);
                  const showOverlay = progress >= 1 && (!areAllUploadsComplete() || progress < 100);
                  
                  return (
                    <div 
                      key={attIndex} 
                      className="file-card" 
                      tabIndex={0} 
                      role="button" 
                      aria-label={`Preview ${attachment.name}`} 
                      data-filename={attachment.name}
                      onClick={() => {
                        // Always show lightbox immediately with attachment info
                        handleAttachmentPreview(attachment);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          // Always show lightbox immediately with attachment info
                          handleAttachmentPreview(attachment);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="file-name" title={attachment.name}>
                        {attachment.name}
                      </div>
                      <span className="file-type-label">{ext}</span>
                      {showOverlay && (
                        <FileProgressOverlay 
                          progress={progress} 
                          isComplete={isComplete} 
                          fileName={attachment.name} 
                        />
                      )}
                    </div>
                  );
                }
              })}
            </div>
          )}
          
          {/* User message bubble with edit button */}
          <div className="user-message-bubble-wrapper">
            <div 
              className={`message-bubble user`}
              onContextMenu={(e) => handleUserMessageRightClick(e, msg.content)}
            >
              <div className="user-message-inner">
                {/* Green Avatar inside the bubble */}
                <div className="user-avatar-inside-bubble">
                  {getUserInitials()}
                </div>
                {/* Message text next to avatar */}
                <div className="user-text-content">
                  {renderMessageContent(msg.content, isUser)}
                </div>
              </div>
            </div>
            {!appState.isProcessing && (
              <button
                className="user-edit-btn"
                onClick={() => handleEditMessage(index, msg.content)}
                title="Edit and resend message"
              >
                <Edit sx={{ fontSize: 14 }} />
              </button>
            )}
          </div>
        </div>
      );
    } else {
      // Ensure the ref array is large enough
      if (!expertContentRefs.current[index]) {
        expertContentRefs.current[index] = null;
      }
      return (
        <div
          key={`message-${msg.id || msg.timestamp || msg.role}-${index}`}
          className={`message-row expert-message-row ${isLastExpertMessage ? 'last-expert-message' : 'previous-expert-message'}`}
        >
          <div className="expert-message-container">
            {/* File Attachments - Render above message content */}
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="file-cards-container" style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
                {msg.attachments.map((attachment, attIndex) => {
                  if (!isFirstMessageWithAttachment(attachment, index, messages)) {
                    return null;
                  }
                  // Get file extension for type label
                  let ext = attachment.name.split('.').pop()?.toUpperCase() || 'FILE';
                  if (ext === attachment.name) ext = 'FILE';
                  
                  // Check if this is an image file
                  const isImageFile = attachment.type?.startsWith('image/') || 
                    ['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP', 'BMP', 'SVG'].includes(ext);
                  
                  if (isImageFile) {
                    const file = getFileFromAttachment(attachment);
                    const progress = getFileUploadProgress(attachment.name);
                    const isComplete = isFileUploadComplete(attachment.name);
                    const showOverlay = progress >= 1 && (!areAllUploadsComplete() || progress < 100);
                    
                    return (
                      <div 
                        key={attIndex} 
                        className="file-card image-card" 
                        tabIndex={0} 
                        role="button" 
                        aria-label={`Preview ${attachment.name}`} 
                        data-filename={attachment.name}
                        onClick={async () => {
                          const file = await getFileFromAttachmentAsync(attachment);
                          if (file) {
                            handleFilePreview(file);
                          } else {
                            handleAttachmentPreview(attachment);
                          }
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const file = await getFileFromAttachmentAsync(attachment);
                            if (file) {
                              handleFilePreview(file);
                            } else {
                              handleAttachmentPreview(attachment);
                            }
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {file ? (
                          <img 
                            className="file-card-img" 
                            src={allFileBlobUrls[`message-${index}-${attIndex}-${file.name}-${file.size}`] || URL.createObjectURL(file)} 
                            alt={file.name} 
                            data-filename={file.name} 
                          />
                        ) : (
                          <ImagePreview 
                            attachment={attachment} 
                            chatId={appState.currentChat?.id || ''} 
                            className="file-card-img" 
                            alt={attachment.name} 
                          />
                        )}
                        {showOverlay && (
                          <FileProgressOverlay 
                            progress={progress} 
                            isComplete={isComplete} 
                            fileName={attachment.name} 
                          />
                        )}
                      </div>
                    );
                  } else {
                    const progress = getFileUploadProgress(attachment.name);
                    const isComplete = isFileUploadComplete(attachment.name);
                    const showOverlay = progress >= 1 && (!areAllUploadsComplete() || progress < 100);
                    
                    return (
                      <div 
                        key={attIndex} 
                        className="file-card" 
                        tabIndex={0} 
                        role="button" 
                        aria-label={`Preview ${attachment.name}`} 
                        data-filename={attachment.name}
                        onClick={async () => {
                          const file = await getFileFromAttachmentAsync(attachment);
                          if (file) {
                            handleFilePreview(file);
                          } else {
                            handleAttachmentPreview(attachment);
                          }
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const file = await getFileFromAttachmentAsync(attachment);
                            if (file) {
                              handleFilePreview(file);
                            } else {
                              handleAttachmentPreview(attachment);
                            }
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="file-name" title={attachment.name}>
                          {attachment.name}
                        </div>
                        <span className="file-type-label">{ext}</span>
                        {showOverlay && (
                          <FileProgressOverlay 
                            progress={progress} 
                            isComplete={isComplete} 
                            fileName={attachment.name} 
                          />
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            )}
            
            {/* Main Expert Response Content */}
            <div className="expert-response-layout">
              {/* Top: Images First (since they arrive first) */}
              <div className="expert-left-side">
                {/* Unified Image Display - handles both streaming and final images seamlessly */}
                {(msg.images && msg.images.length > 0) && (
                  <PreloadedImageDisplay
                    messageId={msg.id}
                    images={msg.images}
                    isStreaming={msg.isStreaming}
                    onImageClick={handleImageLightboxOpen}
                    onImageLoad={handleImageLoad}
                  />
                )}
                
                {/* Bottom: Message Content (appears after images) */}
                <div
                  className="expert-message-content"
                  ref={el => { expertContentRefs.current[index] = el; return; }}
                  style={{ marginTop: (msg.isStreaming && msg.images && msg.images.length > 0) || (!msg.isStreaming && msg.has_images && msg.images && msg.images.length > 0) ? '16px' : '0' }}
                >
                  {renderMessageContent(msg.content, isUser)}
                </div>

                {/* (Event feed moved to avatar status; intentionally no list here) */}
              </div>
              
              {/* Artifact downloads (code_interpreter files) - show above footer on the left */}
              {msg.artifacts && msg.artifacts.length > 0 && (() => {
                const isValidUrl = (value?: string) => {
                  if (!value || typeof value !== 'string') return false;
                  try { new URL(value); return true; } catch { return false; }
                };
                const uniqueByName = new Map<string, { name: string; url: string }>();
                for (const art of msg.artifacts) {
                  if (!art?.name || !isValidUrl(art?.url)) continue;
                  if (art.name.toLowerCase().startsWith('cfile_')) continue; // hide sandbox file-id artifacts
                  if (!uniqueByName.has(art.name)) uniqueByName.set(art.name, art as { name: string; url: string });
                }
                const artifacts = Array.from(uniqueByName.values());
                if (artifacts.length === 0) return null;
                return (
                  <div className="expert-left-side">
                    <div className="expert-artifacts" style={{ marginTop: '8px', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {artifacts.map((art, i) => (
                        <a key={`${msg.id}-art-above-${i}`} href={art.url} target="_blank" rel="noopener noreferrer" className="artifact-link" style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: '6px', textDecoration: 'none', color: '#1D1E1E', background: '#fff'
                        }}>
                          <Download sx={{ fontSize: 16 }} />
                          <span style={{ fontSize: '12px', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={art.name}>
                            {art.name}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Bottom: Footer section - conditional rendering */}
              <div className="expert-footer-section">
                {/* Left side - Avatar and Status */}
                {isLastExpertMessage && (
                  <div className="expert-left-side" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="expert-avatar-container">
                      {isStreaming ? (
                        <img 
                          src="/images/belle-spinner.gif" 
                          alt="Thinking..." 
                          className="belle-avatar spinner"
                        />
                      ) : (
                        <img 
                          src="/images/belle-static.png" 
                          alt="Belle AI" 
                          className="belle-avatar static"
                        />
                      )}
                    </div>
                    {/* Status message next to avatar - show LAST SSE event if available, else fallback to operationStatus */}
                    {(() => {
                      const humanize = (e: UiStreamEvent | undefined): string => {
                        if (!e) return '';
                        const t = e.type || '';
                        if (t === 'response.created') return 'Initializing…';
                        if (t === 'response.in_progress') return 'Thinking…';
                        if (t.startsWith('response.web_search_call')) {
                          if (e.phase === 'searching' || t.endsWith('.searching')) return 'Searching the web…';
                          if (e.phase === 'in_progress' || t.endsWith('.in_progress')) return 'Preparing web search…';
                          if (e.phase === 'completed' || t.endsWith('.completed')) return 'Analyzing search results…';
                          return 'Web search…';
                        }
                        if (t === 'response.output_item.added') return 'Producing output…';
                        if (t === 'response.output_item.done') return 'Output item complete';
                        if (t === 'response.output_text.delta') return 'Writing…';
                        if (t === 'response.completed' || t === 'response.done') return 'Finalizing…';
                        return "Thinking..." //t.replace(/response\.|output_|item\.|content\./g, '').replace(/_/g, ' ');
                      };
                      const evs = (msg as any).events as UiStreamEvent[] | undefined;
                      const filtered = (msg.isStreaming && Array.isArray(evs)) ? evs.filter(e => {
                        const t = (e?.type || '').toLowerCase();
                        if (t === 'stream_start') return false;
                        if (t === 'heartbeat' || t.includes('heartbeat')) return false;
                        if (t.endsWith('.annotation.added') || t.includes('annotation')) return false;
                        return true;
                      }) : undefined;
                      const lastEvent = (filtered && filtered.length > 0) ? filtered[filtered.length - 1] : undefined;
                      const eventText = humanize(lastEvent);
                      const fallbackText = uiState.operationStatus === 'securing-transfer' ? 'Initializing transfer...' :
                        uiState.operationStatus === 'uploading-attachments' ? 'Uploading Attachment(s)...' :
                        uiState.operationStatus === 'thinking' ? 'Thinking...' : '';
                      const statusText = eventText || fallbackText;
                      return statusText ? (
                        <div style={{ fontStyle: 'italic', color: '#666', fontSize: '14px' }}>
                          {statusText}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
                
                {/* Buttons on right */}
                <div className="expert-right-side">
                  <div className="expert-action-buttons">
                    <button
                      className="expert-action-btn copy-btn"
                      onClick={() => handleCopyExpertMessage(index)}
                      title="Copy response"
                    >
                      <ContentCopy sx={{ fontSize: 16 }} />
                    </button>
                    <button
                      className="expert-action-btn retry-btn"
                      onClick={() => retryMessage(index)}
                      title="Retry response"
                    >
                      <Refresh sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                  {/* Warning message - only for last message */}
                  {isLastExpertMessage && (
                    <div className="expert-warning-message">
                      Bell can make mistakes. Please double-check your results.<br/>If your results stop midway, just type continue
                    </div>
                  )}
                </div>
              </div>
              {/* Async expert display status - shown below the spinner during processing */}
              {isLastExpertMessage && isStreaming && msg.displayStatus && !msg.content && (
                <div style={{ fontStyle: 'italic', color: '#888', fontSize: '13px', marginTop: '6px', paddingLeft: '4px' }}>
                  {msg.displayStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  }, [
    renderMessageContent, 
    getFileFromAttachment, 
    getFileUploadProgress, 
    isFileUploadComplete, 
    areAllUploadsComplete,
    getFileFromAttachmentAsync,
    stopResponsePolling,
    retryMessage
  ]);

      const isSubmitDisabled = !message.trim() || !uiState.selectedExpert || hasUploadsInProgress() || uiState.showValidationError;
  const showStopButton = appState.isProcessing;

  // Chat title dropdown handlers
  const handleTitleDropdownToggle = () => {
    setTitleDropdownOpen(!titleDropdownOpen);
  };

  const handleRenameClick = () => {
    if (appState.currentChat) {
      setRenameValue(appState.currentChat.title);
      setRenameDialogOpen(true);
      setTitleDropdownOpen(false);
    }
  };

  const handleStarClick = () => {
    if (appState.currentChat) {
      toggleChatStar(appState.currentChat.id);
      setTitleDropdownOpen(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setTitleDropdownOpen(false);
  };

  const handleRenameConfirm = () => {
    if (appState.currentChat && renameValue.trim()) {
      renameChat(appState.currentChat.id, renameValue.trim());
      setRenameDialogOpen(false);
      setRenameValue('');
    }
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setRenameValue('');
  };

  const handleDeleteConfirm = () => {
    if (appState.currentChat) {
      deleteChat(appState.currentChat.id);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // PromptVault handlers
  const handleUserMessageRightClick = (e: React.MouseEvent, content: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    setSelectedPromptContent(content);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setContextMenuOpen(true);
  };

  const handleEditMessage = (index: number, content: string) => {
    // Don't allow editing while processing
    if (appState.isProcessing) return;
    
    setEditingMessageIndex(index);
    setMessage(content);
    
    // Focus the textarea after a short delay to ensure state has updated
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Place cursor at the end of the text
        textareaRef.current.setSelectionRange(content.length, content.length);
      }
    }, 50);
  };

  const handleCancelEdit = () => {
    setEditingMessageIndex(null);
    setMessage('');
  };

  const handleContextMenuClose = () => {
    setContextMenuOpen(false);
  };

  const handleSaveToPromptVault = () => {
    setContextMenuOpen(false);
    setSavePromptDialogOpen(true);
  };

  const handleSavePromptDialogClose = () => {
    setSavePromptDialogOpen(false);
  };

  const handleSavePromptConfirm = async (name: string, tags?: string[]) => {
    try {
      // Pass the current chat ID as the source when saving
      const sourceChatId = appState.currentChat?.id;
      await savePromptToVault(name, selectedPromptContent, tags, sourceChatId);
    } catch (error) {
      console.error('Error saving prompt:', error);
      throw error;
    }
  };

  const handlePromptVaultToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    
    if (!promptVaultDropdownOpen) {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      
      // Position dropdown so its bottom edge is always flush with the top of the button
      const bottom = window.innerHeight - buttonRect.top - 13; // Distance from bottom of viewport to top of button, adjusted 13px lower
      const left = buttonRect.left; // Align left edge of dropdown with left edge of icon
      
      setPromptVaultPosition({ bottom, left });
    }
    
    setPromptVaultDropdownOpen(!promptVaultDropdownOpen);
    setPromptVaultButtonRef(e.currentTarget);
  };

  const handlePromptVaultClose = () => {
    setPromptVaultDropdownOpen(false);
  };

  const handleSelectPrompt = (content: string) => {
    setMessage(content);
    // Auto-resize the textarea
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.chat-title-dropdown-container')) {
        setTitleDropdownOpen(false);
      }
      // Close context menu when clicking outside
      if (contextMenuOpen && !target.closest('.context-menu')) {
        setContextMenuOpen(false);
      }
    };

    if (titleDropdownOpen || contextMenuOpen) {
      document.addEventListener('click', handleDocumentClick);
      return () => document.removeEventListener('click', handleDocumentClick);
    }
  }, [titleDropdownOpen, contextMenuOpen]);

  // Blob URL cache to prevent excessive creation and enable proper cleanup
  const blobUrlCacheRef = useRef<{ [key: string]: string }>({});
  const prevDepsRef = useRef<{ attachedFiles: File[], messages: any[] }>({ attachedFiles: [], messages: [] });

  // Memoized blob URLs for all files (attached files + message attachment files)
  // This implementation properly manages blob URLs to prevent memory leaks and excessive creation
  const allFileBlobUrls = useMemo(() => {
    const currentDeps = {
      attachedFiles: uiState.attachedFiles,
      messages: appState.currentChat?.messages || []
    };
    
    // Check if dependencies actually changed to avoid unnecessary work
    const attachedFilesChanged = JSON.stringify(currentDeps.attachedFiles.map(f => ({ name: f.name, size: f.size }))) !== 
                                 JSON.stringify(prevDepsRef.current.attachedFiles.map(f => ({ name: f.name, size: f.size })));
    const messagesChanged = currentDeps.messages.length !== prevDepsRef.current.messages.length;
    
    if (!attachedFilesChanged && !messagesChanged) {
      // If nothing changed, return the existing cache
      return blobUrlCacheRef.current;
    }

    const newUrls: { [key: string]: string } = {};
    const oldCache = blobUrlCacheRef.current;
    const usedKeys = new Set<string>();
    
    // Handle attached files
    uiState.attachedFiles.forEach((file, index) => {
      const key = `attached-${file.name}-${file.size}-${index}`;
      usedKeys.add(key);
      
      // Reuse existing blob URL if it exists for the same file
      if (oldCache[key]) {
        newUrls[key] = oldCache[key];
      } else {
        // Only create new blob URL if we don't have one
        newUrls[key] = URL.createObjectURL(file);
      }
    });
    
    // Handle message attachment files
    if (appState.currentChat?.messages) {
      appState.currentChat.messages.forEach((message, msgIndex) => {
        if (message.attachments) {
          message.attachments.forEach((attachment, attIndex) => {
            const file = getFileFromAttachment(attachment);
            if (file) {
              const key = `message-${msgIndex}-${attIndex}-${file.name}-${file.size}`;
              usedKeys.add(key);
              
              // Reuse existing blob URL if it exists for the same file
              if (oldCache[key]) {
                newUrls[key] = oldCache[key];
              } else {
                // Only create new blob URL if we don't have one
                newUrls[key] = URL.createObjectURL(file);
              }
            }
          });
        }
      });
    }
    
    // Clean up unused blob URLs
    Object.keys(oldCache).forEach(key => {
      if (!usedKeys.has(key) && oldCache[key].startsWith('blob:')) {
        URL.revokeObjectURL(oldCache[key]);
      }
    });
    
    // Update cache and previous dependencies
    blobUrlCacheRef.current = newUrls;
    prevDepsRef.current = currentDeps;
    
    return newUrls;
  }, [uiState.attachedFiles, appState.currentChat?.messages]);

  // Cleanup blob URLs when component unmounts or dependencies change
  useEffect(() => {
    return () => {
      // Revoke all blob URLs when component unmounts
      Object.values(blobUrlCacheRef.current).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      blobUrlCacheRef.current = {};
    };
  }, []);

  return (
    <div className="chat-view" ref={chatViewRef}>
      {/* Chat Title Dropdown - only show when in chat view */}
      {appState.currentChat && (
        <div className="chat-title-header" style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          zIndex: 100
        }}>
          <div className="chat-title-dropdown-container" style={{ position: 'relative' }}>
            <button
              className="chat-title-dropdown-button"
              onClick={handleTitleDropdownToggle}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'inherit',
                color: '#333',
                maxWidth: '280px',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#f9f9f9';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              <span style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1
              }}>
                {appState.currentChat.title}
              </span>
              <ExpandMore sx={{ 
                fontSize: 16,
                transform: titleDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </button>

            {titleDropdownOpen && (
              <div
                className="chat-title-dropdown-menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  minWidth: '200px',
                  background: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  marginTop: '4px',
                  overflow: 'hidden'
                }}
              >
                <button
                  className="dropdown-menu-item"
                  onClick={handleRenameClick}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '14px',
                    color: '#333',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background = 'none';
                  }}
                >
                  <Edit sx={{ fontSize: 16 }} />
                  Rename Chat
                </button>
                <button
                  className="dropdown-menu-item"
                  onClick={handleStarClick}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '14px',
                    color: '#333',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background = 'none';
                  }}
                >
                  {appState.currentChat.isStarred ? (
                    <>
                      <Star sx={{ fontSize: 16, color: '#ffc107' }} />
                      Unstar Chat
                    </>
                  ) : (
                    <>
                      <StarBorder sx={{ fontSize: 16 }} />
                      Star Chat
                    </>
                  )}
                </button>
                <button
                  className="dropdown-menu-item"
                  onClick={handleDeleteClick}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '14px',
                    color: '#d32f2f',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background = '#ffeaea';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background = 'none';
                  }}
                >
                  <Delete sx={{ fontSize: 16 }} />
                  Delete Chat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="chat-messages" ref={chatMessagesRef}>
        {/* Memoized message rendering (optimized to prevent unnecessary re-renders) */}
        {useMemo(() => {
          const elements: React.ReactNode[] = [];
          const messages = appState.currentChat?.messages || [];
          let i = 0;
          while (i < messages.length) {
            const msg = messages[i];
            if (msg.role === 'user') {
              // Check if next message is expert
              const nextMsg = messages[i + 1];
              if (nextMsg && nextMsg.role === 'expert') {
                const userKey = msg.id || `${msg.timestamp}-user-${i}`;
                const expertKey = nextMsg.id || `${nextMsg.timestamp}-expert-${i+1}`;
                elements.push(
                  <div className="conversation-pair" key={`pair-${userKey}-${expertKey}-${i}`}>
                    {renderMessage(msg, i, messages)}
                    {renderMessage(nextMsg, i + 1, messages)}
                  </div>
                );
                i += 2;
                continue;
              } else {
                const userKey = msg.id || `${msg.timestamp}-user-${i}`;
                elements.push(
                  <div className="conversation-pair" key={`pair-${userKey}-${i}`}>
                    {renderMessage(msg, i, messages)}
                  </div>
                );
                i++;
                continue;
              }
            } else if (msg.role === 'expert') {
              // Expert message not following a user message
              const expertKey = msg.id || `${msg.timestamp}-expert-${i}`;
              elements.push(
                <div key={`expert-${expertKey}-${i}`}>
                  {renderMessage(msg, i, messages)}
                </div>
              );
              i++;
              continue;
            } else {
              // Other message types (e.g., moderator)
              const otherKey = msg.id || `${msg.timestamp}-${msg.role}-${i}`;
              elements.push(
                <div key={`other-${otherKey}-${i}`}>
                  {renderMessage(msg, i, messages)}
                </div>
              );
              i++;
              continue;
            }
          }
          return elements;
        }, [appState.currentChat?.messages, renderMessage])}
        {/* Chat spacer element - mirroring the original frontend */}
        <div id="chatSpacer" ref={spacerRef} style={{ 
          height: '0px', 
          minHeight: '0px'
        }}></div>
        <div ref={messagesEndRef} />
      </div>

      {/* Validation Message */}
      <div ref={validationMessageRef}>
        <ValidationMessage
          isVisible={uiState.showValidationError}
          errorMessage={uiState.validationError || undefined}
        />
      </div>

      {/* Editing indicator */}
      {editingMessageIndex !== null && (
        <div className="edit-message-banner">
          <span className="edit-message-banner-text">
            <Edit sx={{ fontSize: 14 }} />
            Editing message
          </span>
          <button className="edit-message-cancel-btn" onClick={handleCancelEdit}>
            Cancel
          </button>
        </div>
      )}

      {/* Chat Input Container */}
      <div className="chat-input-outer-container" ref={chatInputContainerRef}>
        <div className="chat-input-container">
          <div className="input-box-styling" style={{ 
            position: 'relative',
            border: isDragOver ? 'none' : undefined
          }}>
            {/* Drag and Drop Overlay - positioned over input box styling only */}
            {isDragOver && (
              <div className="chat-drag-area" style={{
                position: 'absolute',
                top: '-1px',
                left: '-1px',
                right: '-1px',
                bottom: '-1px',
                background: 'white',
                border: '2px dashed var(--primary-color)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                pointerEvents: 'none'
              }}>
                <div className="drag-content" style={{
                  textAlign: 'center',
                  color: 'var(--primary-color)',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  <CloudUpload sx={{ fontSize: 32, marginBottom: '8px' }} />
                  <p style={{ margin: 0 }}>Drop files here to upload</p>
                </div>
              </div>
            )}
            
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={editingMessageIndex !== null 
                ? "Edit your message and press Enter to resend." 
                : "Ask a follow-up question.\nPress Enter to submit, Shift+Enter for a new line."}
              rows={1}
              ref={textareaRef}
            />

            <div style={{ position: 'relative', display: 'inline-block', float: 'left' }}>
              <button
                className="attach-button"
                onClick={handleAttachClick}
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  zIndex: 10,
                  width: '32px',
                  height: '32px',
                  paddingTop: '16px'
                }}
                title="Attach..."
              >
                <Add />
              </button>
              {attachMenuOpen && (
                <div
                  id="chat-attach-dropdown"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    minWidth: 180,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    zIndex: 3000,
                    marginBottom: 4,
                    padding: 0
                  }}
                >
                  <button
                    className="attach-menu-item"
                    onClick={handleFileUpload}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#333',
                      borderRadius: '6px',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.background = 'none';
                    }}
                  >
                    <UploadFile sx={{ fontSize: 16 }} />
                    Upload a File
                  </button>
                  <button
                    className="attach-menu-item"
                    onClick={handleScreenshot}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#333',
                      borderRadius: '6px',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.background = 'none';
                    }}
                  >
                    <Screenshot sx={{ fontSize: 16 }} />
                    Take a Screenshot
                  </button>
                </div>
              )}
            </div>

            {/* PromptVault Button */}
            <div style={{ position: 'relative', display: 'inline-block', float: 'left', marginLeft: '8px' }}>
              <button
                className="prompt-vault-button"
                onClick={handlePromptVaultToggle}
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  zIndex: 10,
                  width: '32px',
                  height: '32px',
                  paddingTop: '16px'
                }}
                title="Load from PromptVault"
              >
                <Inventory />
              </button>
              {promptVaultDropdownOpen && (
                <PromptVaultDropdown
                  open={promptVaultDropdownOpen}
                  onClose={handlePromptVaultClose}
                  onSelectPrompt={handleSelectPrompt}
                  position={promptVaultPosition}
                />
              )}
            </div>

            <button
              className={`submit-button ${showStopButton ? 'stop-mode' : ''}`}
              onClick={showStopButton ? handleStopResponse : handleSubmit}
              disabled={!showStopButton && isSubmitDisabled}
              type="button"
              style={{
                float: 'right',
                position: 'static',
                marginRight: '4px',
                marginTop: '4px',
                marginBottom: '4px',
                background: showStopButton ? '#dc3545' : (isSubmitDisabled ? '#ccc' : 'var(--primary-color)'),
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: showStopButton ? 'pointer' : (isSubmitDisabled ? 'not-allowed' : 'pointer'),
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'background-color 0.2s'
              }}
            >
              {showStopButton ? <Stop /> : <ArrowUpward />}
            </button>

            <div style={{
              float: 'right',
              marginRight: '8px',
              marginTop: '4px',
              zIndex: 10
            }}>
              <ExpertsPopup />
            </div>
          </div>
        </div>

        {/* Attached Files - Render as cards below the input area */}
        {uiState.attachedFiles.length > 0 && (
          <div className="attached-files-area" ref={attachedFilesAreaRef}>
            <div className="attached-files-chips">
              {uiState.attachedFiles.map((file, index) => {
                // Check if this is an image file to apply proper styling
                const isImageFile = file.type?.startsWith('image/') || 
                  ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(
                    file.name.split('.').pop()?.toLowerCase() || ''
                  );
                
                return (
                  <div 
                    key={index} 
                    className={`file-card ${isImageFile ? 'image-card' : ''}`}
                    tabIndex={0} 
                    role="button" 
                    aria-label={`Preview ${file.name}`} 
                    data-filename={file.name}
                    onClick={() => handleFilePreview(file)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFilePreview(file);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {isImageFile ? (
                      <img 
                        className="file-card-img" 
                        src={allFileBlobUrls[`attached-${file.name}-${file.size}-${index}`] || URL.createObjectURL(file)} 
                        alt={file.name} 
                        data-filename={file.name} 
                      />
                    ) : (
                      <>
                        <div className="file-name" title={file.name}>
                          {file.name}
                        </div>
                        <span className="file-type-label">
                          {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                        </span>
                      </>
                    )}
                    {(() => {
                      const progress = getFileUploadProgress(file.name);
                      const isComplete = isFileUploadComplete(file.name);
                      const showOverlay = progress >= 1 && (!areAllUploadsComplete() || progress < 100);
                      return showOverlay && (
                        <FileProgressOverlay 
                          progress={progress} 
                          isComplete={isComplete} 
                          fileName={file.name} 
                        />
                      );
                    })()}
                    <button
                      className="file-card-close-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        file={previewFile}
        attachment={previewAttachment}
        chatId={appState.currentChat?.id || appState.persistentChatId}
      />

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt,.csv,.json,.xml,.yaml,.yml,.xls,.xlsx,.ppt,.pptx,image/*"
      />

      {/* Toast notification for copy */}
      {showCopyToast && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          background: 'var(--primary-color)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: 8,
          fontSize: 16,
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          Response copied to clipboard!
        </div>
      )}

      {/* Rename Dialog */}
      {renameDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '400px',
            maxWidth: '500px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: 600,
              color: '#333'
            }}>
              Rename Chat
            </h3>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameConfirm();
                } else if (e.key === 'Escape') {
                  handleRenameCancel();
                }
              }}
              placeholder="Enter new chat title..."
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '20px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => {
                (e.target as HTMLElement).style.borderColor = 'var(--primary-color)';
              }}
              onBlur={(e) => {
                (e.target as HTMLElement).style.borderColor = '#ddd';
              }}
            />
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleRenameCancel}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  background: 'white',
                  color: '#666',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRenameConfirm}
                disabled={!renameValue.trim()}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: renameValue.trim() ? 'var(--primary-color)' : '#ccc',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: renameValue.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '400px',
            maxWidth: '500px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: 600,
              color: '#d32f2f'
            }}>
              Delete Chat
            </h3>
            <p style={{
              margin: '0 0 20px 0',
              fontSize: '14px',
              color: '#666',
              lineHeight: 1.5
            }}>
              Are you sure you want to delete this chat? This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  background: 'white',
                  color: '#666',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: '#d32f2f',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background = '#b71c1c';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = '#d32f2f';
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxOpen && lightboxImage && (
        <ImageLightbox
          image={lightboxImage}
          imageIndex={lightboxImageIndex}
          isOpen={lightboxOpen}
          onClose={handleImageLightboxClose}
        />
      )}

      {/* Right-click Context Menu */}
      {contextMenuOpen && (
        <div
          style={{
            position: 'fixed',
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            minWidth: '180px',
            overflow: 'hidden'
          }}
        >
          <button
            onClick={handleSaveToPromptVault}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              color: '#333',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.background = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = 'none';
            }}
          >
            <Inventory sx={{ fontSize: 16 }} />
            Save to PromptVault
          </button>
        </div>
      )}

      {/* Save Prompt Dialog */}
      <SavePromptDialog
        open={savePromptDialogOpen}
        onClose={handleSavePromptDialogClose}
        onConfirm={handleSavePromptConfirm}
        promptContent={selectedPromptContent}
      />
    </div>
  );
};

export default ChatView; 